import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import { CalendarDays, ArrowRight, MapPin } from "lucide-react";
import { useI18n } from "@/context/I18nContext";
import { type TradeEvent } from "@/lib/mock/events";

/**
 * Days remaining until a trade fair opens.
 *
 * Rendered CLIENT-SIDE ONLY, and deliberately so. A countdown computed during
 * SSR is stale by the time it reaches the browser and, worse, produces a
 * hydration mismatch: the server writes one number into the HTML and React
 * immediately renders a different one. Returning null until the first effect
 * runs means the server and the first client render agree — on nothing — and
 * the banner appears a frame later.
 *
 * The fair is in İzmir, so the deadline is fixed at +03:00 rather than at the
 * visitor's local midnight. A buyer in Singapore and one in Lisbon should see
 * the same number of days, because the doors open at one moment for both.
 *
 * Three states, and the last two are the reason this is not a bare subtraction:
 *   - before      → days remaining
 *   - during      → "happening now", not a negative number
 *   - after       → nothing at all, so a finished fair does not sit on the
 *                   homepage counting upward into last year
 */
/**
 * Two props, both for the same reason: this bar renders on two surfaces and
 * should not repeat what the surface already says.
 *
 * `showCta` — on the homepage the button is the whole point, it is how a
 * visitor reaches the event page. On the event page that link points at the
 * page you are already reading.
 *
 * `showFacts` — the dates/venue/hall/stand row is essential on the homepage,
 * where nothing else carries them. On the event page the facts strip sits about
 * 400px below and states the same four things as cards, so the bar keeps the
 * one thing only it can say (how long is left) and hands the facts to the
 * cards. Trimming the bar rather than the cards is deliberate: this component
 * is client-only and returns null both before its first effect and after the
 * fair closes, so the cards are what guarantee the facts are always on the page.
 */
export function EventCountdown({
  event,
  showCta = true,
  showFacts = true,
}: {
  event: TradeEvent;
  showCta?: boolean;
  showFacts?: boolean;
}) {
  const { t } = useI18n();
  const [days, setDays] = useState<number | null>(null);
  const [live, setLive] = useState(false);

  useEffect(() => {
    const opens = new Date(`${event.startDate}T00:00:00+03:00`).getTime();
    // The fair runs to the END of its closing day, so the window closes at
    // midnight following endDate rather than at midnight starting it.
    const closes = new Date(`${event.endDate}T23:59:59+03:00`).getTime();

    const tick = () => {
      const now = Date.now();
      if (now > closes) {
        setDays(null);
        setLive(false);
        return;
      }
      if (now >= opens) {
        setLive(true);
        setDays(0);
        return;
      }
      setLive(false);
      setDays(Math.ceil((opens - now) / 86_400_000));
    };

    tick();
    // Hourly is enough for a day counter and costs nothing; a per-second timer
    // would re-render the homepage 3,600 times an hour to change nothing.
    const id = setInterval(tick, 3_600_000);
    return () => clearInterval(id);
  }, [event.startDate, event.endDate]);

  if (days === null && !live) return null;

  return (
    <div className="relative overflow-hidden border-t border-mint/25 bg-gradient-to-r from-navy via-navy to-teal/40 animate-fade-in-up">
      {/* Soft mint bloom drifting behind the bar, so the strip reads as a
          highlight rather than another navy band. Decorative only. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-16 right-[15%] h-40 w-40 rounded-full bg-mint/15 blur-3xl animate-pulse-glow motion-reduce:animate-none"
      />
      <div className="relative mx-auto flex max-w-7xl flex-col items-center gap-4 px-4 py-5 md:flex-row md:justify-between md:px-6">
        <div className="flex flex-col items-center gap-3 text-center sm:flex-row sm:text-start">
          <div className="flex items-center gap-3">
            {live ? (
              <span className="relative flex h-2.5 w-2.5 shrink-0">
                {/* The ping is decoration; reduced-motion users get the dot. */}
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-mint opacity-75 motion-reduce:animate-none" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-mint" />
              </span>
            ) : (
              <span className="flex h-11 min-w-11 items-center justify-center rounded-xl bg-mint px-2.5 font-display text-xl font-extrabold text-navy tabular-nums shadow-lg shadow-mint/25">
                {days}
              </span>
            )}
            <span className="text-sm font-bold text-white sm:text-base">
              {live ? t("countdown.live") : t("countdown.days")}
            </span>
          </div>

          {showFacts && (
            <>
              <span className="hidden h-4 w-px bg-white/20 sm:block" aria-hidden="true" />

              <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-white/70">
                <span className="inline-flex items-center gap-1.5">
                  <CalendarDays className="h-3.5 w-3.5" aria-hidden="true" />
                  {t("expo.facts.dates.value")}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
                  {event.venue} · {t("expo.facts.hall.value")} {event.hall} · {event.stand}
                </span>
              </div>
            </>
          )}
        </div>

        {showCta && (
          <Link
            to="/events/$slug"
            params={{ slug: event.slug }}
            className="group inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-mint px-4 py-2 text-xs font-extrabold text-navy shadow-md shadow-mint/20 transition-all duration-300 hover:bg-mint/90 hover:shadow-mint/40 motion-safe:hover:-translate-y-0.5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-mint"
          >
            {t("countdown.cta")}
            <ArrowRight
              className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5"
              aria-hidden="true"
            />
          </Link>
        )}
      </div>
    </div>
  );
}
